import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { graphLoginRequest } from "../auth/msalConfig";
import { isM365Configured } from "../auth/authMode";
import { useIdToken } from "../auth/useIdToken";
import { fetchMe } from "../stats/api";

export function Header() {
  return (
    <header className="bg-kampmeyer-primary text-white">
      <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-end gap-3">
          <img
            src="/kampmeyer-logo.png"
            alt="Kampmeyer"
            className="h-10 w-auto"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="text-lg font-semibold tracking-tight leading-none pb-0.5">Bildskalierung</div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {isM365Configured && <AuthControls />}
        </div>
      </div>
    </header>
  );
}

const iconBtn =
  "w-9 h-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition";

function ThemeToggle() {
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    try {
      localStorage.setItem("theme", next ? "dark" : "light");
    } catch {
      /* localStorage evtl. nicht verfügbar – Umschalten funktioniert trotzdem */
    }
  };
  return (
    <button onClick={toggle} aria-label="Hell-/Dunkelmodus umschalten" className={iconBtn}>
      {dark ? (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />
        </svg>
      )}
    </button>
  );
}

function AuthControls() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const getIdToken = useIdToken();
  // Nur Admins sehen den Link zur Statistik. Der Server prüft den Zugriff unabhängig davon.
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    fetchMe(getIdToken)
      .then((me) => !cancelled && setIsAdmin(me.isAdmin))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.homeAccountId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const signIn = () => void instance.loginRedirect(graphLoginRequest);
  const switchAccount = () => void instance.loginRedirect({ ...graphLoginRequest, prompt: "select_account" });
  const signOut = () => void instance.logoutRedirect();

  if (!account) {
    return (
      <button
        onClick={signIn}
        className="px-3 py-1.5 rounded bg-white text-kampmeyer-primary font-medium hover:bg-kampmeyer-neutral-100 transition text-sm"
      >
        Mit Microsoft 365 anmelden
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} aria-label="Profil" aria-expanded={open} className={iconBtn}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white text-kampmeyer-neutral-900 rounded-lg shadow-lg border border-kampmeyer-neutral-100 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-kampmeyer-neutral-100">
            {account.name && <div className="text-sm font-medium truncate">{account.name}</div>}
            <div className="text-xs text-kampmeyer-neutral-500 truncate">{account.username}</div>
          </div>
          {isAdmin && (
            <a
              href={window.location.pathname.startsWith("/admin") ? "/" : "/admin"}
              className="block px-4 py-2.5 text-sm font-medium hover:bg-kampmeyer-neutral-100 transition border-b border-kampmeyer-neutral-100"
            >
              {window.location.pathname.startsWith("/admin") ? "Zur Bildskalierung" : "Statistik (Admin)"}
            </a>
          )}
          <button
            onClick={switchAccount}
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-kampmeyer-neutral-100 transition"
          >
            Konto wechseln
          </button>
          <button
            onClick={signOut}
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-kampmeyer-neutral-100 transition"
          >
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
}
